import type { OnInit} from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { MatSortModule } from '@angular/material/sort';
import { CycleManagementService } from '../../core/services/cycle-management.service';
import { CustomTooltipDirective } from '../../shared/directives/custom-tooltip.directive';
import { AuthService } from '../../core/services/auth.service';
import { getProfileImageUrl } from '../../shared/utils/profile-image.util';
import { DefaultImageDirective } from '../../shared/directives/default-image.directive';
import type { CycleAssignmentWithUser, CycleDetails, CycleManager } from './models';

@Component({
  selector: 'app-cycle-details',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,  imports: [
    CommonModule,
    NgOptimizedImage,
    RouterModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatProgressBarModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    MatSortModule,
    DefaultImageDirective,
    CustomTooltipDirective
  ],
  templateUrl: './cycle-details.component.html',
  styleUrl: './cycle-details.component.scss'
})
export class CycleDetailsComponent implements OnInit {
  cycleId = 0;
  isComplianceBP = false;

  // Profile image utility
  getProfileImageUrl = getProfileImageUrl;

  private _managers: CycleManager[] = [];

  set managers(value: CycleManager[]) {
    this._managers = value.map(m => ({
      ...m,
      profileUrl: getProfileImageUrl(m.user_prid)
    } as CycleManager));
    this.cdr.markForCheck();
  }

  get managers(): CycleManager[] {
    return this._managers;
  }

  cycleDetails: CycleDetails = {
    id: 0,
    cycleName: '',
    marketName: '',
    createdBy: '',
    totalManagers: 0,
    totalNoAnswers: 0,
    deadline: '',
    assignedAt: ''
  };

  displayedColumns: string[] = [
    'userId',
    'progress', // Added progress column
    'noAnswers',
    'deadline',
    'marketName',
    'assignedBy',
    'assignedAt',
    'updatedAt'
  ];

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private cycleManagementService = inject(CycleManagementService);
  private authService = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    const id = this.route.snapshot.params['id'];
    this.cycleId = id ? Number(id) : 0;

    // Check if user is Compliance BP
    const roleContext = this.authService.getSelectedRoleContext();
    this.isComplianceBP = roleContext?.role_code?.toUpperCase() === 'COMPBP';

    if (this.cycleId) {
      this.loadCycleDetails();
      this.loadCycleAssignments();
    }
  }

  /**
   * Load cycle details to get total_no_answers and other cycle information
   */
  loadCycleDetails(): void {
    this.cycleManagementService.getCycleDetail(this.cycleId).subscribe({
      next: (response) => {
        // Populate cycle details from the response
        this.cycleDetails = {
          id: response.id,
          cycleName: response.cycle_name,
          marketName: response.market_name || '-',
          createdBy: response.created_by || '-',
          totalManagers: this.cycleDetails.totalManagers || 0, // Will be updated by assignments
          totalNoAnswers: response.total_no_answers || 0,
          deadline: this.formatDate(response.default_deadline),
          assignedAt: this.cycleDetails.assignedAt || '-' // Will be updated by assignments if available
        };
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Failed to fetch cycle details:', err);
      }
    });
  }

  loadCycleAssignments(): void {
    // Get current user's role context
    const roleContext = this.authService.getSelectedRoleContext();

    // If user is BU Admin, filter by their business unit IDs
    let businessUnitIds: number[] | undefined;
    if (roleContext?.role_code?.toUpperCase() === 'BUADMIN' && roleContext.business_units?.length) {
      // Extract business unit IDs from business_units array
      businessUnitIds = roleContext.business_units.map(bu => bu.business_unit_id);
    }

    this.cycleManagementService.getCycleAssignments(this.cycleId, businessUnitIds).subscribe({
      next: (response) => {
        if (response.assignments && response.assignments.length > 0) {
          // Populate cycle details from first assignment
          const firstAssignment = response.assignments[0];
          this.cycleDetails = {
            ...this.cycleDetails,
            id: firstAssignment.cycle_id,
            cycleName: firstAssignment.cycle_name,
            marketName: firstAssignment.market_name,
            createdBy: firstAssignment.assigned_by,
            totalManagers: new Set(response.assignments.map(a => a.user_id)).size,
            deadline: this.formatDate(firstAssignment.default_deadline),
            assignedAt: this.formatDate(firstAssignment.assigned_at)
          };

          // Map assignments to manager rows
          this.managers = response.assignments.map((assignment: CycleAssignmentWithUser) => {
            const completed = assignment.completed_assignments || 0;
            // Use completion_percentage from backend if available, otherwise calculate manually
            const progress = assignment.completion_percentage|| 0;
            let statusClass = 'status-low';
            if (progress >= 100) {
              statusClass = 'status-complete';
            } else if (progress >= 70) {
              statusClass = 'status-high';
            } else if (progress >= 30) {
              statusClass = 'status-medium';
            }
            return {
              id: assignment.id,
              userId: assignment.user_id,
              user_display_name: assignment.user_display_name || `User ${assignment.user_id}`,
              user_prid: assignment.user_prid || '',
              user_email: assignment.user_email || '',
              cycleId: assignment.cycle_id,
              cycleName: assignment.cycle_name,
              marketName: assignment.market_name,
              bu_unit_id: assignment.bu_unit_id,
              bu_unit_name: assignment.bu_unit_name,
              bu_role_name: assignment.bu_role_name,
              formsCount: assignment.form_count,
              noAnswers: assignment.total_no_answers || 0,
              deadline: this.formatDate(assignment.custom_deadline || assignment.default_deadline),
              assignedBy: assignment.assigned_by,
              assignedAt: this.formatDate(assignment.assigned_at),
              updatedAt: assignment.updated_at ? this.formatDate(assignment.updated_at) : '-',
              completed_assignments: completed,
              completion_percentage: assignment.completion_percentage,
              progress,
              statusClass,
              number_of_submitted_activities: assignment.number_of_submitted_activities || 0,
              pendingApprovals: assignment.number_of_submitted_activities || 0
            };
          });
        }

        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Failed to fetch cycle assignments:', err);
        this.cdr.markForCheck();
      }
    });
  }

  private formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).replace(/ /g, '-');
  }

  backToHome(): void {
    this.router.navigate(['/view-cycles']);
  }

  navigateToCycles(): void {
    this.router.navigate(['/view-cycles']);
  }

  getCompletionStatusClass(status: number): string {
    if (status <= 25) return 'status-low';
    if (status <= 50) return 'status-medium';
    if (status <= 75) return 'status-high';
    return 'status-complete';
  }

  getCompletionStatusText(status: number): string {
    return `${status}% Complete`;
  }

  editManager(_manager: CycleManager): void {
    // Implement edit functionality
  }

  viewManager(manager: CycleManager): void {
    this.router.navigate(['/manager-cycle-form', this.cycleId, manager.userId], {
      state: { managerName: manager.user_display_name }
    });
  }

  onManagerRowClick(manager: CycleManager): void {
    this.router.navigate(['/manager-cycle-form', this.cycleId, manager.userId], {
      state: { managerName: manager.user_display_name }
    });
  }
}
